import { useLocale } from './context.tsx';
import { useT } from './context.tsx';
import { LOCALE_NATIVE_LABELS, SUPPORTED_LOCALES } from './types.ts';

/**
 * Compact header control for flipping between `zh-CN` and `en`. Renders
 * as a pill cluster where the active locale is highlighted with the
 * primary token and the other option sits on `surface-container-lowest`.
 *
 * Label resolution uses `LOCALE_NATIVE_LABELS` (exported from `types.ts`)
 * so every option always renders in its own script regardless of the
 * active locale. These labels are intentionally NOT in the translation
 * dictionaries — they are UI chrome, not content.
 *
 * The persisted choice survives page reloads via `localStorage` (see
 * `context.tsx`); this component is a pure UI on top of that setter.
 */
export function LocaleSwitcher(): JSX.Element {
  const { locale, setLocale } = useLocale();
  const t = useT();
  return (
    <div
      role="group"
      aria-label={t('i18n.switcher.aria_label')}
      data-testid="locale-switcher"
      className="inline-flex items-center gap-0 rounded-lg bg-surface-container-lowest p-1"
    >
      {SUPPORTED_LOCALES.map((target) => {
        const isActive = locale === target;
        return (
          <button
            key={target}
            type="button"
            aria-pressed={isActive}
            data-testid={`locale-switcher-${target}`}
            onClick={() => {
              if (!isActive) {
                setLocale(target);
              }
            }}
            className={[
              'rounded-md px-2.5 py-1 text-[11px] font-semibold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-high',
              isActive
                ? 'bg-primary-container text-on-primary'
                : 'text-outline hover:text-on-surface',
            ].join(' ')}
          >
            {LOCALE_NATIVE_LABELS[target]}
          </button>
        );
      })}
    </div>
  );
}
